import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function SimonsTownPage() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      document.title =
        'Simons Town Painters Cape Town | Historic Naval Town Painting Services | False Bay Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Professional painting services in Simons Town, Cape Town. Specialist heritage restoration and coastal painting for False Bay properties. Expert marine-grade coatings and historic preservation.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Simons Town painters, heritage painting Cape Town, False Bay coastal painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/simons-town`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Simons Town Painters Cape Town',
        description: 'Professional painting services in Simons Town, Cape Town',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/simons-town`,
        areaServed: {
          '@type': 'Place',
          name: 'Simons Town, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -34.1925,
            longitude: 18.4347,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error setting up meta tags or JSON‑LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript && existingScript.textContent?.includes('Simons Town Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Heritage Property Restoration',
      description:
        'Specialized restoration services for Simons Town\'s historic Victorian and Georgian buildings, preserving architectural integrity while providing modern protection. Our expert team understands the importance of maintaining the character of these significant properties while ensuring they withstand coastal conditions. We use period-appropriate techniques combined with contemporary marine-grade materials for optimal results.',
      features: [
        'Victorian restoration',
        'Georgian architecture',
        'Period-appropriate colors',
        'Heritage compliance',
        'Architectural preservation',
        'Modern protection systems'
      ],
    },
    {
      icon: 'ri-home-4-line',
      title: 'Coastal Residential Painting',
      description:
        'Comprehensive painting services for Simons Town homes with marine-grade coatings designed to withstand False Bay\'s harsh coastal environment. Our protective systems defend against salt air, UV exposure, and moisture while maintaining beautiful aesthetics that complement the town\'s historic character and natural harbor setting.',
      features: [
        'Marine-grade coatings',
        'Salt air protection',
        'UV resistance',
        'Moisture barriers',
        'Harbor property expertise',
        'Long-term durability'
      ],
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial & Naval District Painting',
      description:
        'Professional painting for Simons Town\'s shops, restaurants, guesthouses, and commercial properties along Main Road and the harbor front. We understand the importance of maintaining the town\'s historic charm while meeting modern business needs, offering flexible scheduling and efficient execution to minimize operational disruption.',
      features: [
        'Historic district expertise',
        'After-hours service',
        'Minimal disruption',
        'Heritage color schemes',
        'Tourism-ready finishes',
        'Professional standards'
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Exterior Coastal Painting',
      description:
        'Comprehensive exterior painting services specifically designed for Simons Town\'s False Bay coastal environment and naval heritage. Our weather-resistant systems protect against salt spray, wind, and intense sun exposure while maintaining the authentic character that makes this historic naval town so special and visually appealing.',
      features: [
        'Weather-resistant systems',
        'Coastal protection',
        'Wind resistance',
        'Sun protection',
        'Color retention',
        'Extended warranty'
      ],
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Interior Period Painting',
      description:
        'Expert interior painting services for Simons Town properties featuring period-appropriate finishes, custom color consultation, and meticulous attention to historic detail. We specialize in recreating authentic Victorian and Georgian interior schemes while using modern, eco-friendly materials that are safe and durable for contemporary living.',
      features: [
        'Period finishes',
        'Historic color matching',
        'Decorative techniques',
        'Low-VOC materials',
        'Furniture protection',
        'Detailed craftsmanship'
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Waterproofing & Maintenance',
      description:
        'Comprehensive waterproofing solutions and maintenance services for Simons Town properties experiencing coastal weather damage or aging. Our expert approach addresses both aesthetic and structural concerns, including crack repair, damp treatment, and protective coatings to ensure your property remains beautiful and protected against False Bay\'s marine environment.',
      features: [
        'Damp treatment',
        'Crack restoration',
        'Waterproofing systems',
        'Preventive maintenance',
        'Structural protection',
        'Long-term solutions'
      ],
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Heritage Property Assessment',
      description:
        'Comprehensive evaluation of your Simons Town property, including inspection of historic features, coastal exposure, and existing paint condition. We assess architectural significance, salt air impact, and moisture issues to develop a customized strategy that preserves heritage value while providing modern coastal protection.',
    },
    {
      step: 2,
      title: 'Period-Appropriate Material Selection',
      description:
        'Expert consultation on paints and coatings that respect historic character while providing superior coastal protection. We recommend products that match period color palettes and finishes while incorporating modern marine-grade technology for durability. Our selections balance authenticity with contemporary performance requirements.',
    },
    {
      step: 3,
      title: 'Careful Surface Preparation',
      description:
        'Thorough preparation respecting historic surfaces, including gentle cleaning, careful repairs, and appropriate primer application. Our detailed prep work preserves original features while ensuring optimal adhesion and longevity. We use techniques appropriate for heritage properties to maintain architectural integrity.',
    },
    {
      step: 4,
      title: 'Expert Application',
      description:
        'Professional application by experienced craftsmen trained in both heritage restoration and modern coastal painting techniques. We employ period-appropriate methods combined with contemporary quality standards, ensuring proper coating systems with adequate drying times for maximum durability and authentic appearance.',
    },
    {
      step: 5,
      title: 'Quality Inspection',
      description:
        'Thorough quality control inspection of all painted surfaces, checking for uniform coverage, proper adhesion, and authentic finish. We ensure every detail meets both heritage standards and our quality expectations, with comprehensive documentation of all work performed for heritage compliance and future reference.',
    },
    {
      step: 6,
      title: 'Maintenance Guidance',
      description:
        'Detailed maintenance recommendations and care instructions specific to heritage properties in coastal environments. We provide guidance on cleaning, inspection schedules, and preventive maintenance that respects historic character while maximizing the lifespan of your coating system in Simons Town\'s marine climate.',
    },
  ];

  const recentProjects = [
    {
      title: 'Victorian Naval Officer\'s Residence',
      location: 'Simons Town Historic District',
      description:
        'Complete restoration of an 1890s Victorian home including exterior facade restoration with period-appropriate colors, interior decorative finishes, and modern marine-grade protection systems. Project preserved original architectural details while providing contemporary weather resistance and structural protection.',
      image:
        'https://public.readdy.ai/ai/img_res/d5b5e110173d53244f04dbb2fcbb384b.jpg',
    },
    {
      title: 'Harbor Front Guesthouse Renovation',
      location: 'Simons Town Main Road',
      description:
        'Comprehensive painting of a historic guesthouse including 8 guest rooms, common areas, and exterior facades. Project completed using flexible scheduling to accommodate guests, with heritage-appropriate color schemes and specialized coatings for high-traffic hospitality areas and coastal exposure.',
      image:
        'https://readdy.ai/api/search-image?query=Historic%20guesthouse%20painting%20in%20Simons%20Town%2C%20harbor%20front%20accommodation%20property%2C%20professional%20hospitality%20painting%20Cape%20Town%2C%20charming%20coastal%20guesthouse%20with%20heritage%20features&width=400&height=300&seq=simons-town-project-2&orientation=landscape',
    },
    {
      title: 'Georgian Cottage Restoration',
      location: 'Simons Town Upper Village',
      description:
        'Meticulous restoration of a rare Georgian cottage including exterior weatherboard painting, interior period finishes, and comprehensive waterproofing. Project required specialized techniques to preserve the cottage\'s historic character while addressing modern comfort and coastal protection needs.',
      image:
        'https://readdy.ai/api/search-image?query=Georgian%20cottage%20restoration%20painting%20in%20Simons%20Town%2C%20historic%2018th%20century%20home%20with%20period%20features%2C%20professional%20heritage%20restoration%20painting%2C%20charming%20coastal%20cottage%20Cape%20Town&width=400&height=300&seq=simons-town-project-3&orientation=landscape',
    },
    {
      title: 'Main Road Commercial Building',
      location: 'Simons Town Commercial District',
      description:
        'Complete exterior and interior painting of a historic commercial building housing retail shops and offices. Included facade restoration with heritage-appropriate colors, modern interior finishes, and specialized coatings for coastal protection. Completed with minimal business disruption.',
      image:
        'https://readdy.ai/api/search-image?query=Historic%20commercial%20building%20painting%20in%20Simons%20Town%20Main%20Road%2C%20heritage%20retail%20property%20with%20shops%2C%20professional%20commercial%20painting%20Cape%20Town%2C%20charming%20naval%20town%20architecture&width=400&height=300&seq=simons-town-project-4&orientation=landscape',
    },
  ];

  const testimonials = [
    {
      name: 'Elizabeth Cartwright',
      location: 'Simons Town Victorian Home',
      rating: 5,
      text:
        'Exceptional restoration of our Victorian home. The team understood the importance of preserving historic character while providing modern protection. The period-appropriate colors and finishes are perfect, and the marine-grade coatings have performed excellently for three years. Highly recommend for heritage properties.',
    },
    {
      name: 'Robert Daniels',
      location: 'Simons Town Guesthouse',
      rating: 5,
      text:
        'Outstanding service for our harbor front guesthouse. They worked around our guests with minimal disruption and the results are beautiful. The heritage color scheme perfectly complements our building\'s character and our guests love the fresh, authentic appearance. Professional and reliable.',
    },
    {
      name: 'Margaret Fourie',
      location: 'Simons Town Cottage',
      rating: 5,
      text:
        'The restoration of our Georgian cottage exceeded all expectations. They handled the historic features with great care and expertise. The combination of period authenticity and modern coastal protection is exactly what we needed. The craftsmanship is superb and the attention to detail remarkable.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Historic%20Simons%20Town%20harbor%20and%20Victorian%20buildings%20Cape%20Town%20being%20painted%2C%20heritage%20naval%20town%20with%20False%20Bay%20views%2C%20professional%20painters%20working%20on%20historic%20coastal%20properties%2C%20charming%20seaside%20architecture%20with%20mountain%20backdrop&width=1920&height=600&seq=simons-town-hero-main&orientation=landscape"
            alt="Professional painting services in Simons Town, Cape Town"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Heritage <span className="text-yellow-400">Simons Town Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for Simons Town&apos;s historic properties.
              Specialized heritage restoration and marine‑grade coatings for this charming False Bay naval town with rich architectural heritage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expert Painting Services for Simons Town&apos;s Historic Naval Community
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Simons Town is one of Cape Town&apos;s most historically significant suburbs, renowned for its rich naval heritage, beautifully preserved Victorian and Georgian architecture, and stunning False Bay harbor setting. This charming coastal town demands painting services that understand both the importance of heritage preservation and the unique challenges of marine environments.
              </p>
              <p>
                Our specialized painting services for Simons Town properties combine heritage restoration expertise, quality marine-grade materials, and deep understanding of False Bay coastal environmental factors. The town&apos;s historic buildings require careful attention to architectural authenticity while the coastal location demands robust protection against salt air, UV radiation, and moisture-rich conditions that can accelerate deterioration.
              </p>
              <p>
                Whether you own a Victorian home in the historic district, manage a guesthouse along Main Road, operate a business in the commercial area, or have a cottage with harbor views, our team delivers painting solutions that preserve heritage character while providing modern protection. We understand that Simons Town properties represent important historical and financial investments, and our services are designed to enhance their value through authentic restoration and superior coastal protection.
              </p>
              <p>
                The unique character of Simons Town, with its concentration of heritage buildings, naval history, and active tourism industry, creates specific painting requirements. From preserving the authentic appearance of Victorian facades to maintaining the charm of Georgian cottages, our team has the specialized expertise and experience to handle heritage projects with the professionalism and attention to detail that these significant properties deserve.
              </p>
              <p>
                Simons Town&apos;s position on False Bay creates a distinctive microclimate with strong winds, salt spray, and intense sun exposure that can be particularly challenging for historic buildings with traditional construction methods. Our approach combines respect for original materials and techniques with contemporary marine-grade protection systems, ensuring these important buildings are preserved for future generations while withstanding the demanding coastal environment. We work closely with heritage authorities and property owners to ensure all work meets conservation standards while delivering practical, long-lasting results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Heritage Painting Services in Simons Town
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Simons Town&apos;s historic properties, coastal homes, and commercial buildings. Our services are tailored to preserve the heritage character of this significant naval town.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Heritage Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Understanding Simons Town&apos;s Heritage and Coastal Painting Challenges
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Simons Town&apos;s historic buildings present unique challenges that require specialized knowledge and careful approach. Many properties date from the Victorian and Georgian eras, featuring traditional construction methods and materials that must be respected during restoration. Modern painting techniques must be adapted to work with these historic surfaces while providing effective protection against contemporary environmental challenges.
              </p>
              <p>
                The town&apos;s False Bay coastal location creates demanding environmental conditions for painted surfaces. Salt-laden air from the harbor affects properties throughout the area, causing accelerated deterioration of paint systems if not properly addressed. Historic buildings with traditional lime-based renders and original woodwork are particularly vulnerable to salt damage and require specialized protective coatings that allow surfaces to breathe while providing marine-grade protection.
              </p>
              <p>
                UV radiation intensity in Simons Town is significant due to the reflective properties of False Bay and the area&apos;s sunny climate. Historic pigments and traditional paint formulations are especially susceptible to UV degradation, making careful selection of period-appropriate yet UV-resistant coatings essential for maintaining authentic appearance while ensuring longevity. Our expertise in heritage color matching combined with modern UV protection technology delivers optimal results.
              </p>
              <p>
                Moisture management is particularly critical for Simons Town&apos;s heritage properties due to the combination of ocean humidity, wind-driven rain, and the breathable nature of traditional building materials. Modern impermeable coatings can trap moisture in historic walls, causing serious damage. Our approach uses vapor-permeable coating systems specifically designed for heritage buildings in coastal environments, allowing moisture to escape while providing weather protection and maintaining the integrity of original construction materials.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Heritage Painting Process for Simons Town Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring authentic restoration and lasting protection for historic properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Simons Town Properties Choose Our Heritage Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Heritage Restoration Expertise',
                    description:
                      'Specialized knowledge of Victorian and Georgian architecture with proven experience in heritage conservation. Our team understands the specific requirements for preserving historic character while providing modern coastal protection for significant properties.',
                  },
                  {
                    title: 'Coastal Protection Systems',
                    description:
                      'Advanced marine-grade coatings designed for False Bay conditions while respecting heritage requirements. We use vapor-permeable systems that protect historic buildings without trapping moisture or compromising traditional construction materials.',
                  },
                  {
                    title: 'Period-Appropriate Finishes',
                    description:
                      'Expert color matching and authentic finish techniques that recreate historic appearance using modern, durable materials. We combine traditional craftsmanship with contemporary technology for results that are both authentic and long-lasting.',
                  },
                  {
                    title: 'Heritage Compliance',
                    description:
                      'Deep understanding of heritage regulations and conservation standards. We work closely with heritage authorities to ensure all work meets required standards while delivering practical, effective results for property owners.',
                  },
                  {
                    title: 'Local Community Knowledge',
                    description:
                      'Strong connections to Simons Town\'s community and respect for the town\'s naval heritage and historic character. We understand the importance of preserving what makes this town special while meeting contemporary needs.',
                  },
                  {
                    title: 'Comprehensive Documentation',
                    description:
                      'Detailed documentation of all restoration work for heritage compliance and future reference. Our records provide valuable information for ongoing maintenance and future conservation efforts, protecting your property\'s heritage value.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20historic%20Simons%20Town%20property%2C%20Victorian%20heritage%20home%20restoration%2C%20quality%20painting%20equipment%20and%20materials%2C%20coastal%20property%20maintenance%20Cape%20Town%20with%20False%20Bay%20views&width=600&height=700&seq=simons-town-team-work&orientation=portrait"
                alt="Heritage painting services in Simons Town"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Simons Town Heritage Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our heritage restoration projects in Simons Town, demonstrating our commitment to
              preserving the historic character of this significant naval town
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recentProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Simons Town Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied heritage property owners in Simons Town
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Restore Your Simons Town Heritage Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a heritage consultation and detailed quote. Experience specialized
            painting services designed to preserve Simons Town&apos;s historic character.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}